import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Fuse from "fuse.js";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, Sparkles, ChevronRight } from "lucide-react";
import { memberDomainLabel } from "@/lib/domainLabel";
import { stripDocIdPrefix } from "@/lib/docTitle";
import { docTypeLabel, stripStartHerePrefix } from "@/lib/docTypeLabel";

const displayTitle = (title: string | null | undefined) =>
  stripStartHerePrefix(stripDocIdPrefix(title));
import type { LibraryRow } from "@/components/library/LibrarySearch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Category labels are the merged member-facing labels from
// memberDomainLabel (single source of truth in src/lib/domainLabel.ts),
// so the Library, document-page chips, and ?section= deep links can
// never drift. See AGENTS.md rule 4b.
function displayCategoryLabel(
  collection: string | undefined | null,
  domain: string | null | undefined,
): string {
  return memberDomainLabel(domain, collection === "faq" ? "faq" : "sop");
}

// Curated member-facing section order. Anything not listed sorts after
// "Investments and Insurance" alphabetically.
const CURATED_ORDER = [
  "How This Library Works",
  "Foundations",
  "Religious Covenant",
  "Governance",
  "Recordkeeping",
  "Entity Structure",
  "Banking",
  "Finance",
  "Taxes and the IRS",
  "Assets",
  "Real Estate and Vehicles",
  "Investments and Insurance",
  "People and Membership",
  "Sample Documents",
  "When Something Goes Wrong",
];
const SAMPLES_LABEL = "Sample Documents";
const SAFETY_NET_RANK =
  CURATED_ORDER.indexOf("Investments and Insurance") + 0.5;
function categoryRank(label: string): number {
  const i = CURATED_ORDER.indexOf(label);
  return i >= 0 ? i : SAFETY_NET_RANK;
}

type SemanticResult = {
  id: string;
  doc_id: string;
  collection: string;
  domain: string;
  title: string;
  similarity: number;
  score: number;
};

type CollectionFilter = "all" | "faq" | "sop" | "samples";

function pathFor(row: { collection?: string; doc_id: string; slug?: string | null }) {
  if (row.collection === "faq" && row.doc_id === "FAQ-000") return "/support/library/dictionary";
  const seg = (row.slug && row.slug.trim()) || row.doc_id;
  return row.collection === "faq" ? `/support/library/${seg}` : `/support/library/${seg}`;
}

function CollectionBadge({ collection }: { collection?: string }) {
  const isFaq = collection === "faq";
  return (
    <Badge
      variant="outline"
      className={`text-[10px] font-medium shrink-0 ${
        isFaq
          ? "border-gold/60 text-gold-foreground bg-gold/10"
          : "border-emerald-600/50 text-emerald-800 bg-emerald-50 dark:text-emerald-200 dark:bg-emerald-950/40"
      }`}
    >
      {docTypeLabel(collection)}
    </Badge>
  );
}

const STATE_KEY = "support_library_state_v1";
type PersistedState = {
  q: string;
  collectionFilter: CollectionFilter;
  openSections: Record<string, boolean>;
  scrollY: number;
};
function loadPersisted(): Partial<PersistedState> {
  try {
    return JSON.parse(sessionStorage.getItem(STATE_KEY) || "{}");
  } catch {
    return {};
  }
}

type SampleDoc = {
  id: string;
  title: string;
  description: string | null;
  storage_path: string;
  sort_order: number;
};

export default function SupportLibrary({ onAskStew }: { onAskStew: () => void }) {
  const persisted = useRef<Partial<PersistedState>>(loadPersisted()).current;
  const [allRows, setAllRows] = useState<LibraryRow[]>([]);
  const [samples, setSamples] = useState<SampleDoc[]>([]);
  const [openingSampleId, setOpeningSampleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [q, setQ] = useState(persisted.q || "");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [semantic, setSemantic] = useState<SemanticResult[] | null>(null);
  const [semLoading, setSemLoading] = useState(false);
  const [collectionFilter, setCollectionFilter] = useState<CollectionFilter>(
    persisted.collectionFilter || "all",
  );
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    persisted.openSections || {},
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [targetSection, setTargetSection] = useState<string | null>(null);
  const restoredScrollRef = useRef(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError(false);
      const [docsRes, samplesRes] = await Promise.all([
        supabase
          .from("sop_documents" as any)
          .select("id,doc_id,domain,title,version,status,trigger_tags,sort_order,body_markdown,collection,slug")
          .eq("status", "published")
          .order("sort_order", { ascending: true }),
        supabase
          .from("sample_documents" as any)
          .select("id,title,description,storage_path,sort_order")
          .eq("is_active", true)
          .order("sort_order", { ascending: true }),
      ]);
      if (docsRes.error) {
        console.error("SupportLibrary load failed:", docsRes.error);
        setLoadError(true);
      } else {
        setAllRows((docsRes.data as unknown as LibraryRow[]) || []);
      }
      if (samplesRes.error) {
        console.error("SupportLibrary samples load failed:", samplesRes.error);
      } else {
        setSamples((samplesRes.data as unknown as SampleDoc[]) || []);
      }
      setLoading(false);
    })();
  }, []);

  async function openSample(sample: SampleDoc) {
    setOpeningSampleId(sample.id);
    try {
      const { data, error } = await supabase.storage
        .from("sample-documents")
        .createSignedUrl(sample.storage_path, 300);
      if (error || !data?.signedUrl) throw error;
      window.open(data.signedUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Sample open failed:", err);
    } finally {
      setOpeningSampleId(null);
    }
  }

  const scopedRows = useMemo(() => {
    // FMT is an internal-only structural bucket. Never render to members.
    const visible = allRows.filter(
      (r) => !((r.collection || "sop") !== "faq" && r.domain === "FMT"),
    );
    if (collectionFilter === "all") return visible;
    if (collectionFilter === "samples") return [];
    return visible.filter((r) => (r.collection || "sop") === collectionFilter);
  }, [allRows, collectionFilter]);

  const fuse = useMemo(
    () =>
      new Fuse(scopedRows, {
        keys: [
          { name: "title", weight: 0.5 },
          { name: "domain", weight: 0.2 },
          { name: "trigger_tags", weight: 0.3 },
        ],
        threshold: 0.4,
        ignoreLocation: true,
        minMatchCharLength: 2,
      }),
    [scopedRows],
  );

  // Catalog id matches: exact first, then prefix. Case-insensitive.
  const docIdMatches = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/\s+/g, "");
    if (!s) return [] as LibraryRow[];
    const exact = scopedRows.filter((r) => (r.doc_id || "").toLowerCase() === s);
    const prefix = scopedRows.filter(
      (r) => (r.doc_id || "").toLowerCase() !== s && (r.doc_id || "").toLowerCase().startsWith(s),
    );
    return [...exact, ...prefix];
  }, [q, scopedRows]);

  const fuzzy = useMemo(() => {
    const s = q.trim();
    if (!s) return [] as LibraryRow[];
    const found = fuse.search(s).map((r) => r.item);
    const seen = new Set(docIdMatches.map((r) => r.id));
    return [...docIdMatches, ...found.filter((r) => !seen.has(r.id))].slice(0, 8);
  }, [q, fuse, docIdMatches]);

  const keyword = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [] as LibraryRow[];
    const rest = scopedRows.filter(
      (r) =>
        !docIdMatches.some((m) => m.id === r.id) &&
        (r.title?.toLowerCase().includes(s) ||
          (r.doc_id || "").toLowerCase().includes(s) ||
          (r.trigger_tags || "").toLowerCase().includes(s) ||
          (r.body_markdown || "").toLowerCase().includes(s)),
    );
    return [...docIdMatches, ...rest];
  }, [q, scopedRows, docIdMatches]);


  useEffect(() => {
    const s = q.trim();
    if (s.length < 4) {
      setSemantic(null);
      return;
    }
    const t = setTimeout(async () => {
      setSemLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("search-docs", {
          body: { query: s },
        });
        if (error) throw error;
        const results = (data as any)?.results as SemanticResult[] | undefined;
        setSemantic(Array.isArray(results) ? results : []);
      } catch (err) {
        console.error("SupportLibrary semantic search failed:", err);
        setSemantic(null);
      } finally {
        setSemLoading(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Grouped browse view: merged FAQs + SOPs, grouped by the display-only
  // category map above so labels never drift and can never touch DB values.
  const grouped = useMemo(() => {
    const byLabel = new Map<string, LibraryRow[]>();
    for (const r of scopedRows) {
      const label = displayCategoryLabel(r.collection, r.domain);
      if (!byLabel.has(label)) byLabel.set(label, []);
      byLabel.get(label)!.push(r);
    }
    for (const arr of byLabel.values()) {
      arr.sort(
        (a, b) =>
          (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.doc_id.localeCompare(b.doc_id),
      );
    }

    const sections: {
      key: string;
      label: string;
      docs: LibraryRow[];
    }[] = [];
    for (const [label, docs] of byLabel.entries()) {
      sections.push({ key: `d:${label}`, label, docs });
    }
    sections.sort(
      (a, b) =>
        categoryRank(a.label) - categoryRank(b.label) ||
        a.label.localeCompare(b.label),
    );
    return sections;
  }, [scopedRows]);

  // If arriving with ?section=<label>, expand that section (collapse others)
  // and scroll it into view. Then clear the param so it doesn't stick around.
  useEffect(() => {
    if (loading) return;
    const raw = searchParams.get("section");
    if (!raw) return;
    const known = grouped.some((g) => g.label === raw);
    if (known) setTargetSection(raw);
    const params = new URLSearchParams(searchParams);
    params.delete("section");
    setSearchParams(params, { replace: true });
  }, [loading, grouped, searchParams, setSearchParams]);

  // Restore scroll position once after data + sections are ready.
  useEffect(() => {
    if (loading || restoredScrollRef.current) return;
    const y = persisted.scrollY;
    if (typeof y === "number" && y > 0) {
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
    restoredScrollRef.current = true;
  }, [loading, persisted.scrollY]);

  // Persist list state (search, filter, expanded sections) as it changes.
  useEffect(() => {
    try {
      const prev = loadPersisted();
      sessionStorage.setItem(
        STATE_KEY,
        JSON.stringify({
          q,
          collectionFilter,
          openSections,
          scrollY: prev.scrollY || 0,
        }),
      );
    } catch {}
  }, [q, collectionFilter, openSections]);

  // Capture scroll on unmount and just before a doc link click.
  const captureScroll = () => {
    try {
      const prev = loadPersisted();
      sessionStorage.setItem(
        STATE_KEY,
        JSON.stringify({
          q: prev.q ?? q,
          collectionFilter: prev.collectionFilter ?? collectionFilter,
          openSections: prev.openSections ?? openSections,
          scrollY: window.scrollY,
        }),
      );
    } catch {}
  };
  useEffect(() => {
    return () => captureScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);





  const showResults = q.trim().length > 0;
  const semanticList = semantic
    ? semantic
        .filter((r) =>
          collectionFilter === "all" ? true : (r.collection || "sop") === collectionFilter,
        )
        .map((r) => {
          const full = allRows.find((x) => x.id === r.id);
          return full
            ? {
                id: full.id,
                doc_id: full.doc_id,
                title: full.title,
                collection: full.collection,
                domain: full.domain,
              }
            : {
                id: r.id,
                doc_id: r.doc_id,
                title: r.title,
                collection: r.collection,
                domain: r.domain,
              };
        })
    : null;
  const resultList = semanticList
    ? [
        ...docIdMatches.map((r) => ({
          id: r.id,
          doc_id: r.doc_id,
          title: r.title,
          collection: r.collection,
          domain: r.domain,
        })),
        ...semanticList.filter((r) => !docIdMatches.some((m) => m.id === r.id)),
      ]
    : keyword.map((r) => ({
        id: r.id,
        doc_id: r.doc_id,
        title: r.title,
        collection: r.collection,
        domain: r.domain,
      }));


  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setDropdownOpen(true);
      setHighlight((h) => Math.min(h + 1, fuzzy.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (dropdownOpen && fuzzy[highlight]) {
        e.preventDefault();
        setDropdownOpen(false);
        navigate(pathFor(fuzzy[highlight]));
      }
    } else if (e.key === "Escape") {
      setDropdownOpen(false);
    }
  }

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground"
      >
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        <span className="text-sm">Gathering the library.</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm">
          The library didn't load just now. Refresh in a moment, and if it keeps
          happening, ask Stew or open a ticket.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onAskStew}
        className="w-full text-left text-sm text-muted-foreground hover:text-foreground rounded-md border border-dashed border-muted-foreground/30 px-3 py-2 flex items-center gap-2"
      >
        <Sparkles className="h-3.5 w-3.5 text-gold" aria-hidden="true" />
        Not sure where to look? Ask Stew and he will point you to the right page.
      </button>

      <div className="relative" ref={containerRef}>
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10"
          aria-hidden="true"
        />
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setDropdownOpen(true);
            setHighlight(0);
          }}
          onFocus={() => setDropdownOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Search the library"
          className="pl-9 w-full"
          aria-label="Search the library"
        />
        {dropdownOpen && fuzzy.length > 0 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg max-h-96 overflow-y-auto">
            {fuzzy.map((r, i) => (
              <Link
                key={r.id}
                to={pathFor(r)}
                onClick={() => setDropdownOpen(false)}
                onMouseEnter={() => setHighlight(i)}
                className={`flex items-center gap-2 px-3 py-2 text-sm ${
                  i === highlight ? "bg-muted" : "hover:bg-muted/60"
                }`}
              >
                <span className="truncate flex-1">{displayTitle(r.title)}</span>
                <CollectionBadge collection={r.collection} />
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Filter by collection">
        {([
          { key: "all", label: "All" },
          { key: "faq", label: "FAQ" },
          { key: "sop", label: "Step by Step" },
          { key: "samples", label: "Samples" },
        ] as { key: CollectionFilter; label: string }[]).map((chip) => {
          const active = collectionFilter === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setCollectionFilter(chip.key)}
              className={`text-xs rounded-full border px-3 py-1 transition-colors ${
                active
                  ? "bg-foreground text-background border-foreground"
                  : "bg-background text-muted-foreground hover:text-foreground border-muted-foreground/30 hover:border-foreground/50"
              }`}
            >
              {chip.label}
            </button>
          );
        })}
        {!showResults && (
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const labels: string[] = grouped.map((g) => g.label);
                if (samples.length > 0 && collectionFilter !== "faq" && collectionFilter !== "sop") {
                  labels.push(SAMPLES_LABEL);
                }
                const next: Record<string, boolean> = {};
                for (const l of labels) next[l] = true;
                setOpenSections((prev) => ({ ...prev, ...next }));
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >
              Expand all
            </button>
            <span className="text-muted-foreground/40 text-xs">·</span>
            <button
              type="button"
              onClick={() => {
                const labels: string[] = grouped.map((g) => g.label);
                if (samples.length > 0) labels.push(SAMPLES_LABEL);
                const next: Record<string, boolean> = {};
                for (const l of labels) next[l] = false;
                setOpenSections((prev) => ({ ...prev, ...next }));
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >
              Collapse all
            </button>
          </div>
        )}
      </div>

      {showResults ? (
        <Card className="p-4 md:p-6">
          <div className="flex items-baseline gap-3 mb-3">
            <h2 className="text-lg font-serif font-semibold">
              {semantic ? "Best matches" : "Search results"}
            </h2>
            <span className="text-xs text-muted-foreground">{resultList.length}</span>
            {semLoading && (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden="true" />
            )}
          </div>
          {resultList.length === 0 ? (
            <div className="text-center text-muted-foreground py-8 text-sm">
              Nothing matched that yet. Try different words, or ask Stew.
            </div>
          ) : (
            <ul className="divide-y">
              {resultList.map((d) => (
                <li key={d.id}>
                  <Link
                    to={pathFor(d)}
                    onClick={captureScroll}
                    className="flex items-center gap-3 py-2 hover:bg-muted/40 rounded px-2 -mx-2"
                  >
                    <div className="text-sm md:text-base font-medium truncate flex-1">
                      {displayTitle(d.title)}
                    </div>
                    <CollectionBadge collection={d.collection} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : (
        <div className="space-y-2">
          {(() => {
            type Entry =
              | { kind: "docs"; key: string; label: string; docs: LibraryRow[] }
              | { kind: "samples"; key: string; label: string };
            const entries: Entry[] = grouped.map((g) => ({
              kind: "docs",
              key: g.key,
              label: g.label,
              docs: g.docs,
            }));
            if (samples.length > 0 && collectionFilter !== "faq" && collectionFilter !== "sop") {
              entries.push({
                kind: "samples",
                key: "samples",
                label: SAMPLES_LABEL,
              });
            }
            entries.sort(
              (a, b) =>
                categoryRank(a.label) - categoryRank(b.label) ||
                a.label.localeCompare(b.label),
            );
            if (entries.length === 0) {
              return (
                <div className="text-center text-muted-foreground py-12 text-sm">
                  Nothing to show here yet.
                </div>
              );
            }
            return entries.map((e) => {
              const isTarget = targetSection != null && e.label === targetSection;
              // All sections closed by default on first view. Persisted open
              // state (from a prior expand) and ?section= target both win.
              const persistedOpen = openSections[e.label];
              const open =
                persistedOpen !== undefined
                  ? persistedOpen
                  : isTarget
                  ? true
                  : false;
              if (e.kind === "samples") {
                return (
                  <SampleDocumentsSection
                    key={e.key}
                    samples={samples}
                    open={open}
                    onOpenChange={(v) =>
                      setOpenSections((prev) => ({ ...prev, [e.label]: v }))
                    }
                    onOpen={openSample}
                    openingId={openingSampleId}
                    scrollIntoView={isTarget}
                  />
                );
              }
              return (
                <LibrarySection
                  key={e.key}
                  label={e.label}
                  docs={e.docs}
                  open={open}
                  onOpenChange={(v) =>
                    setOpenSections((prev) => ({ ...prev, [e.label]: v }))
                  }
                  onDocClick={captureScroll}
                  scrollIntoView={isTarget}
                />
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}

function LibrarySection({
  label,
  docs,
  open,
  onOpenChange,
  onDocClick,
  scrollIntoView: shouldScroll,
}: {
  label: string;
  docs: LibraryRow[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onDocClick?: () => void;
  scrollIntoView?: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (shouldScroll && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [shouldScroll]);
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <Card ref={cardRef} className="overflow-hidden">
        <CollapsibleTrigger className="w-full flex items-center justify-between gap-3 px-4 md:px-6 py-3 hover:bg-muted/40 transition-colors text-left">
          <div className="flex items-baseline gap-3 min-w-0">
            <h2 className="text-base md:text-lg font-serif font-semibold truncate">{label}</h2>
            <span className="text-xs text-muted-foreground shrink-0">{docs.length}</span>
          </div>
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${
              open ? "rotate-90" : ""
            }`}
            aria-hidden="true"
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="divide-y border-t">
            {docs.map((d) => {
              const isIntro = /-INTRO$/i.test(d.doc_id);
              return (
                <li key={d.id}>
                  <Link
                    to={pathFor(d)}
                    onClick={onDocClick}
                    className={`flex items-center gap-3 px-4 md:px-6 py-2.5 transition-colors ${
                      isIntro
                        ? "bg-gold/10 hover:bg-gold/15 border-l-2 border-gold"
                        : "hover:bg-muted/40"
                    }`}
                  >
                    {isIntro && (
                      <Badge className="bg-gold text-charcoal hover:bg-gold/80 font-mono text-[10px] shrink-0">
                        START HERE
                      </Badge>
                    )}
                    <div
                      className={`text-sm md:text-base truncate flex-1 ${
                        isIntro ? "font-medium" : ""
                      }`}
                    >
                      {displayTitle(d.title)}
                    </div>
                    <CollectionBadge collection={d.collection} />
                  </Link>
                </li>
              );
            })}
          </ul>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

function SampleDocumentsSection({
  samples,
  open,
  onOpenChange,
  onOpen,
  openingId,
  scrollIntoView: shouldScroll,
}: {
  samples: SampleDoc[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onOpen: (s: SampleDoc) => void;
  openingId: string | null;
  scrollIntoView?: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (shouldScroll && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [shouldScroll]);
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <Card ref={cardRef} className="overflow-hidden">
        <CollapsibleTrigger className="w-full flex items-center justify-between gap-3 px-4 md:px-6 py-3 hover:bg-muted/40 transition-colors text-left">
          <div className="flex items-baseline gap-3 min-w-0">
            <h2 className="text-base md:text-lg font-serif font-semibold truncate">
              Sample Documents
            </h2>
            <span className="text-xs text-muted-foreground shrink-0">{samples.length}</span>
          </div>
          <ChevronRight
            className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${
              open ? "rotate-90" : ""
            }`}
            aria-hidden="true"
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 md:px-6 py-2 text-xs text-muted-foreground border-t">
            Finished examples you can read and model after. They show what a completed
            instrument looks like for a fictional association, Cornerstone Stewardship
            Assembly. Your own documents are prepared through your portal workflows or
            by hand from these models.
          </div>
          <ul className="divide-y border-t">
            {samples.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onOpen(s)}
                  disabled={openingId === s.id}
                  className="w-full flex items-center gap-3 px-4 md:px-6 py-2.5 hover:bg-muted/40 transition-colors text-left disabled:opacity-60"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm md:text-base truncate">{s.title}</div>
                    {s.description && (
                      <div className="text-xs text-muted-foreground truncate">
                        {s.description}
                      </div>
                    )}
                  </div>
                  {openingId === s.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-[10px] font-medium shrink-0 border-muted-foreground/40 text-muted-foreground"
                    >
                      PDF
                    </Badge>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
